import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { useHasAnyRole } from '@/auth/hooks';
import { AppRole } from '@/auth/roles';
import { useBulkContentModerationOverride } from '../api';
import { RejectWithPoliciesDialog } from './RejectWithPoliciesDialog';

interface BulkContentModerationActionsProps {
  /** TipMi post IDs for the current selection. */
  contentIds: number[];
  /** Clears selection after a successful bulk action (or when the user clears). */
  onClearSelection: () => void;
}

/**
 * Toolbar actions for multi-select Approve / Reject on Content + Human Review lists.
 * Uses the same content override API as the detail page.
 */
export function BulkContentModerationActions({
  contentIds,
  onClearSelection,
}: BulkContentModerationActionsProps) {
  const confirm = useConfirm();
  const bulkMut = useBulkContentModerationOverride();
  const [rejectOpen, setRejectOpen] = useState(false);

  const canModerate = useHasAnyRole([
    AppRole.Administrator,
    AppRole.SystemAdmin,
    AppRole.ContentManager,
  ]);

  if (!canModerate || contentIds.length === 0) return null;

  const count = contentIds.length;
  const pending = bulkMut.isPending;

  async function onApprove() {
    const { confirmed } = await confirm({
      title: `Approve ${count} item${count === 1 ? '' : 's'}`,
      description:
        'Approve the selected content. Each item will leave the human-review queue and become eligible for publication.',
      confirmText: 'Approve',
    });
    if (!confirmed) return;
    try {
      const result = await bulkMut.mutateAsync({
        contentIds,
        data: { action: 'FORCE_APPROVE' },
      });
      if (result.failed === 0) {
        toast.success(
          result.total === 1
            ? 'Content approved.'
            : `Approved ${result.succeeded} items.`,
        );
      } else {
        toast.warning(`Approved ${result.succeeded} of ${result.total}; ${result.failed} failed.`);
      }
      onClearSelection();
    } catch {
      toast.error('Failed to approve selected content.');
    }
  }

  async function onRejectWithPolicies(result: {
    violatedPolicies: string[];
    notes?: string;
  }) {
    try {
      const outcome = await bulkMut.mutateAsync({
        contentIds,
        data: {
          action: 'FORCE_REJECT',
          violatedPolicies: result.violatedPolicies,
          ...(result.notes ? { reason: result.notes } : {}),
        },
      });
      setRejectOpen(false);
      if (outcome.failed === 0) {
        toast.success(
          outcome.total === 1
            ? 'Content rejected.'
            : `Rejected ${outcome.succeeded} items.`,
        );
      } else {
        toast.warning(
          `Rejected ${outcome.succeeded} of ${outcome.total}; ${outcome.failed} failed.`,
        );
      }
      onClearSelection();
    } catch {
      toast.error('Failed to reject selected content.');
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {count} selected
        </span>
        <Button size="sm" disabled={pending} onClick={() => void onApprove()}>
          <CheckCircle2 className="size-4" />
          Approve
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={pending}
          onClick={() => setRejectOpen(true)}
        >
          <XCircle className="size-4" />
          Reject
        </Button>
        <Button size="sm" variant="ghost" disabled={pending} onClick={onClearSelection}>
          Clear
        </Button>
      </div>

      <RejectWithPoliciesDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        pending={pending}
        title={`Reject ${count} item${count === 1 ? '' : 's'}`}
        description={
          count === 1
            ? 'Select which Community Guidelines Policies this content violated. The post will leave the review queue and be hidden from users.'
            : `Select which Community Guidelines Policies the selected ${count} items violated. Each post will leave the review queue and be hidden from users.`
        }
        onConfirm={onRejectWithPolicies}
      />
    </>
  );
}
